/**
 * Destaques do portal: a lista das últimas matérias de IA e tech do site do
 * tenant, com um botão por linha que preenche a peça (título, material,
 * imagem, link, chapéu). Zero endpoint novo: usa `/posts` e `/categories`,
 * os mesmos da tela de Artigos.
 */
import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';

import { Button } from '@/components/atoms';
import { articlesApi, categoriesApi } from '@/services/articles';
import { formatNewsDate } from '@/utils/datetime';

import styles from '../Social.module.css';
import { type MaterialDaPeca, categoriasIaTech, materialDoPost, resumoDoDestaque } from './materia';

interface Props {
  site: string;
  onEscolher: (m: MaterialDaPeca) => void;
}

export function DestaquesPortal({ site, onEscolher }: Props) {
  const catsQ = useQuery({
    queryKey: ['social-destaques-categorias', site],
    queryFn: () => categoriesApi.list(site || undefined),
    staleTime: 10 * 60_000,
  });
  const cats = useMemo(() => catsQ.data ?? [], [catsQ.data]);
  const sugeridas = useMemo(() => categoriasIaTech(cats), [cats]);

  // Pré-seleção = IA e tech. O operador pode tirar ou pôr qualquer categoria.
  const [ativas, setAtivas] = useState<number[]>([]);
  useEffect(() => {
    setAtivas(sugeridas.map((c) => c.id));
  }, [sugeridas]);

  const postsQ = useQuery({
    queryKey: ['social-destaques-posts', site, ativas],
    queryFn: () => articlesApi.list({ site: site || undefined, categories: ativas, per_page: 8 }),
    enabled: ativas.length > 0,
    staleTime: 60_000,
  });
  const posts = postsQ.data?.items ?? [];
  const [carregando, setCarregando] = useState<number | null>(null);
  const [erro, setErro] = useState<string | null>(null);

  async function usar(id: number) {
    setErro(null);
    setCarregando(id);
    try {
      const post = await articlesApi.get(id, site || undefined);
      onEscolher(materialDoPost(post, cats));
    } catch (e) {
      setErro(e instanceof Error ? e.message : String(e));
    } finally {
      setCarregando(null);
    }
  }

  // Sem site no escopo: começo de conta, não incidente. A tela de Artigos
  // explica como cadastrar; aqui só não ocupamos espaço.
  if (catsQ.data === null || (catsQ.isSuccess && cats.length === 0)) return null;

  const comPosts = cats.filter((c) => c.count > 0);

  return (
    <section className={styles.destaques} aria-label="Destaques do portal">
      <div className={styles.destaquesHead}>
        <strong>Destaques do portal</strong>
        <span className={styles.mutedSmall}>
          clique em “Usar” e a matéria vira o material da peça
        </span>
      </div>
      <div className={styles.destaquesCats}>
        {comPosts.map((c) => {
          const on = ativas.includes(c.id);
          return (
            <button
              key={c.id}
              type="button"
              className={on ? styles.catChipOn : styles.catChip}
              onClick={() => setAtivas((s) => (on ? s.filter((i) => i !== c.id) : [...s, c.id]))}
            >
              {c.name}
            </button>
          );
        })}
      </div>
      {ativas.length === 0 && <p className={styles.mutedSmall}>Escolha ao menos uma categoria.</p>}
      {postsQ.isLoading && <p className={styles.mutedSmall}>Buscando matérias…</p>}
      {erro && <p className={styles.errLine}>{erro}</p>}
      {posts.length > 0 && (
        <ul className={styles.destaquesLista}>
          {posts.map((a) => (
            <li key={a.id}>
              <span className={styles.mutedSmall}>{formatNewsDate(a.published_at)}</span>
              <span className={styles.destaqueTitulo}>{resumoDoDestaque(a)}</span>
              {!a.featured_media && <span className={styles.mutedSmall}>sem imagem</span>}
              <Button
                size="sm"
                variant="secondary"
                disabled={carregando !== null}
                onClick={() => usar(a.id)}
              >
                {carregando === a.id ? '…' : 'Usar'}
              </Button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
